import { AgGridReact } from "ag-grid-react";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchReportingStore } from "../../../store/hazardousMaterialStore";
import { SearchFiltersType, ReportResultType } from "../../../types/api";
import { useLocationStore } from "../../../store/masterDataStore";
import { useHazardMaterialCategoryStore } from "../../../store/masterDataStore";
import { HazardMaterialCategoryType } from "../../../types/api";

const reportTypes = [
  { value: "expiry", label: "Materials Nearing Expiry" },
  { value: "incident", label: "Incident Trends by Severity" },
  { value: "movement", label: "Monthly Movement & Usage Log" },
  { value: "compliance", label: "Compliance Checklist Results" },
];

const incidentSeverities = ["Minor", "Moderate", "Major", "Critical"];
// const materialTypes = ["Chemical", "Pharmaceutical", "Fuel", "Biological"];
const movementTypes = ["In", "Out", "Disposal"];
const complianceStatuses = ["Pass", "Fail", "Attention"];

const InputField = ({
  label,
  children,
  htmlFor,
}: {
  label: string;
  children: React.ReactNode;
  htmlFor?: string;
}) => (
  <div className="flex flex-col">
    <label
      htmlFor={htmlFor}
      className="mb-1 font-medium text-gray-700 select-none"
    >
      {label}
    </label>
    {children}
  </div>
);

const initialFilters: SearchFiltersType = {
  reportType: "expiry",
  casNo: "",
  materialType: "",
  materialName: "",
  storageLocation: "",
  expiryBefore: "",
  expiryAfter: "",
  incidentFrom: "",
  incidentTo: "",
  incidentSeverity: "",
  incidentType: "",
  month: "",
  year: "",
  fromLocation: "",
  toLocation: "",
  movedBy: "",
  receivedBy: "",
  quantityMin: "",
  quantityMax: "",
  checklistTemplate: "",
  inspectionFrom: "",
  inspectionTo: "",
  complianceStatus: "",
  department: "",
  inspectorName: "",
  issueFound: "",
};

const SearchAndReporting: React.FC = () => {
  const [filters, setFilters] = useState<SearchFiltersType>(initialFilters);

  const [resultData, setResultData] = useState<ReportResultType[]>([]);
  const [loading, setLoading] = useState(false);

  const { runSearchReport } = useSearchReportingStore();
  const { allLocations, fetchAllLocations } = useLocationStore();
  const { hazardMaterialCategory, fetchAllHazardMaterialCategory } =
    useHazardMaterialCategoryStore();
  useEffect(() => {
    if (!allLocations.length) fetchAllLocations();
    if (!hazardMaterialCategory.length) fetchAllHazardMaterialCategory();
  }, [allLocations, hazardMaterialCategory]);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (e.target.name === "reportType") {
      setFilters({
        ...initialFilters,
        [e.target.name]: e.target.value as
          | "expiry"
          | "incident"
          | "movement"
          | "compliance",
      });
    } else {
      setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  const runSearch = async () => {
    setLoading(true);
    const data = await runSearchReport(filters);
    setResultData(data);
    setLoading(false);
  };

  const columnDefs = useMemo(() => {
    switch (filters.reportType) {
      case "expiry":
        return [
          { field: "materialName", headerName: "Material", minWidth: 120 },
          { field: "casNumber", headerName: "CAS No.", minWidth: 120 },
          // { field: "materialType", headerName: "Type", minWidth: 120 },
          { field: "expiryDate", headerName: "Expiry Date", minWidth: 130 },
          // { field: "storageLocation", headerName: "Location", minWidth: 130 },
        ];
      case "incident":
        return [
          { field: "incidentId", headerName: "Incident ID", minWidth: 100 },
          { field: "date", headerName: "Date", minWidth: 120 },
          { field: "severity", headerName: "Severity", minWidth: 120 },
          { field: "type", headerName: "Type", minWidth: 120 },
          { field: "department", headerName: "Dept/Location", minWidth: 120 },
          { field: "material", headerName: "Material", minWidth: 120 },
        ];
      case "movement":
        return [
          { field: "movementId", headerName: "Movement ID", minWidth: 110 },
          { field: "date", headerName: "Date", minWidth: 120 },
          { field: "material", headerName: "Material", minWidth: 120 },
          { field: "fromLocation", headerName: "From", minWidth: 120 },
          { field: "toLocation", headerName: "To", minWidth: 120 },
          { field: "quantity", headerName: "Quantity", minWidth: 100 },
          { field: "type", headerName: "Type", minWidth: 100 },
        ];
      case "compliance":
        return [
          { field: "inspectionId", headerName: "Inspection ID", minWidth: 110 },
          { field: "date", headerName: "Date", minWidth: 120 },
          {
            field: "checklistTemplate",
            headerName: "Checklist",
            minWidth: 120,
          },
          { field: "status", headerName: "Status", minWidth: 120 },
          { field: "department", headerName: "Dept", minWidth: 120 },
          { field: "inspector", headerName: "Inspector", minWidth: 120 },
        ];
      default:
        return [];
    }
  }, [filters.reportType]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-gray-900">
        Search & Reporting
      </h1>

      {/* Filter Card */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-10">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 items-end"
        >
          {/* Report Type */}
          <InputField label="Report Type" htmlFor="reportType">
            <select
              id="reportType"
              name="reportType"
              value={filters.reportType}
              onChange={handleFilterChange}
              className="input-field"
              autoFocus
            >
              {reportTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </InputField>

          {/* Expiry Filters */}
          {filters.reportType === "expiry" && (
            <>
              <InputField label="CAS No." htmlFor="casNo">
                <input
                  id="casNo"
                  name="casNo"
                  type="text"
                  value={filters.casNo}
                  onChange={handleFilterChange}
                  className="input-field"
                  placeholder="CAS Number"
                />
              </InputField>
              <InputField label="Material Category" htmlFor="materialType">
                <select
                  id="materialType"
                  name="materialType"
                  value={filters.materialType}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  {hazardMaterialCategory.map(
                    (mt: HazardMaterialCategoryType) => (
                      <option key={mt._id} value={mt._id}>
                        {mt.hazardMaterialCategoryName}
                      </option>
                    )
                  )}
                </select>
              </InputField>
              <InputField label="Storage Location" htmlFor="storageLocation">
                <select
                  id="storageLocation"
                  name="storageLocation"
                  value={filters.storageLocation}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select Location</option>
                  {allLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.room}
                    </option>
                  ))}
                </select>
              </InputField>
              <InputField label="Expiry Before" htmlFor="expiryBefore">
                <input
                  id="expiryBefore"
                  name="expiryBefore"
                  type="date"
                  value={filters.expiryBefore}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Expiry After" htmlFor="expiryAfter">
                <input
                  id="expiryAfter"
                  name="expiryAfter"
                  type="date"
                  value={filters.expiryAfter}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
            </>
          )}

          {/* Incident Filters */}
          {filters.reportType === "incident" && (
            <>
              <InputField label="Incident Date From" htmlFor="incidentFrom">
                <input
                  id="incidentFrom"
                  name="incidentFrom"
                  type="date"
                  value={filters.incidentFrom}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Incident Date To" htmlFor="incidentTo">
                <input
                  id="incidentTo"
                  name="incidentTo"
                  type="date"
                  value={filters.incidentTo}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Severity" htmlFor="incidentSeverity">
                <select
                  id="incidentSeverity"
                  name="incidentSeverity"
                  value={filters.incidentSeverity}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  {incidentSeverities.map((sev) => (
                    <option key={sev} value={sev}>
                      {sev}
                    </option>
                  ))}
                </select>
              </InputField>
              <InputField label="Incident Type" htmlFor="incidentType">
                <input
                  id="incidentType"
                  name="incidentType"
                  type="text"
                  value={filters.incidentType}
                  onChange={handleFilterChange}
                  className="input-field"
                  placeholder="Type"
                />
              </InputField>
              <InputField label="Material Name" htmlFor="materialName">
                <input
                  id="materialName"
                  name="materialName"
                  type="text"
                  value={filters.materialName}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Department" htmlFor="department">
                <input
                  id="department"
                  name="department"
                  type="text"
                  value={filters.department}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
            </>
          )}

          {/* Movement Filters */}
          {filters.reportType === "movement" && (
            <>
              <InputField label="Month" htmlFor="month">
                <input
                  id="month"
                  name="month"
                  type="month"
                  value={filters.month}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Material Name" htmlFor="materialName">
                <input
                  id="materialName"
                  name="materialName"
                  type="text"
                  value={filters.materialName}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="From Location" htmlFor="fromLocation">
                <input
                  id="fromLocation"
                  name="fromLocation"
                  type="text"
                  value={filters.fromLocation}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="To Location" htmlFor="toLocation">
                <input
                  id="toLocation"
                  name="toLocation"
                  type="text"
                  value={filters.toLocation}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Movement Type" htmlFor="movementType">
                <select
                  id="movementType"
                  name="movementType"
                  value={filters.movementType}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  {movementTypes.map((mt) => (
                    <option key={mt} value={mt}>
                      {mt}
                    </option>
                  ))}
                </select>
              </InputField>
            </>
          )}

          {/* Compliance Filters */}
          {filters.reportType === "compliance" && (
            <>
              <InputField label="Inspection From" htmlFor="inspectionFrom">
                <input
                  id="inspectionFrom"
                  name="inspectionFrom"
                  type="date"
                  value={filters.inspectionFrom}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Inspection To" htmlFor="inspectionTo">
                <input
                  id="inspectionTo"
                  name="inspectionTo"
                  type="date"
                  value={filters.inspectionTo}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField
                label="Checklist Template"
                htmlFor="checklistTemplate"
              >
                <input
                  id="checklistTemplate"
                  name="checklistTemplate"
                  type="text"
                  value={filters.checklistTemplate}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Compliance Status" htmlFor="complianceStatus">
                <select
                  id="complianceStatus"
                  name="complianceStatus"
                  value={filters.complianceStatus}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  {complianceStatuses.map((cs) => (
                    <option key={cs} value={cs}>
                      {cs}
                    </option>
                  ))}
                </select>
              </InputField>
              <InputField label="Department" htmlFor="department">
                <input
                  id="department"
                  name="department"
                  type="text"
                  value={filters.department}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Inspector Name" htmlFor="inspectorName">
                <input
                  id="inspectorName"
                  name="inspectorName"
                  type="text"
                  value={filters.inspectorName}
                  onChange={handleFilterChange}
                  className="input-field"
                />
              </InputField>
              <InputField label="Issue Found" htmlFor="issueFound">
                <select
                  id="issueFound"
                  name="issueFound"
                  value={filters.issueFound}
                  onChange={handleFilterChange}
                  className="input-field"
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </InputField>
            </>
          )}

          {/* Buttons */}
          <div className="flex justify-end md:col-span-4 gap-4 mt-6">
            <button
              type="submit"
              className="bg-[#00A8A8] text-white px-6 py-3 rounded-full flex items-center gap-2 hover:bg-teal-600 transition"
            >
              <Search size={18} /> Search
            </button>
            <button
              type="button"
              onClick={() => alert("Download feature to be implemented")}
              className="bg-[#375998] text-white px-6 py-3 rounded-full hover:bg-indigo-700 transition"
            >
              Download
            </button>
          </div>
        </form>
      </div>

      {/* Result Table */}
      <div className="ag-theme-alpine rounded-lg shadow-md p-4">
        <AgGridReact
          rowData={resultData}
          columnDefs={columnDefs}
          headerHeight={65}
          rowHeight={50}
          loading={loading}
          defaultColDef={{ editable: false }}
          domLayout="autoHeight"
          pagination
          paginationPageSize={10}
        />
      </div>

      {/* Styles for Inputs */}
      <style>{`
        .input-field {
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          transition: border-color 0.2s;
        }
        .input-field:focus {
          outline: none;
          border-color: #00a8a8;
          box-shadow: 0 0 0 3px rgba(0, 168, 168, 0.3);
        }
      `}</style>
    </div>
  );
};

export default SearchAndReporting;

// Example exportReport function (CSV or other formats)
const exportReport = () => {
  // Implement CSV/PDF generation using resultData
  // Can use libraries like papaparse or jsPDF
  // Example: papaparse.unparse(resultData) and download
};
